/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
} from "@chakra-ui/react";
import moment from "moment";
import { useEffect, useState } from "react";
import DisplayCaseInformation from "./DisplaySchema";

import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { FaLink } from "react-icons/fa6";
import { MdDragIndicator } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";

/* ----------------------------------
   Sortable Card
----------------------------------- */
const SortableCard = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    marginBottom: "8px",
  };

  return (
    <Box ref={setNodeRef} style={style}>
      {children({ attributes, listeners })}
    </Box>
  );
};

/* ----------------------------------
   Main Component
----------------------------------- */
const CaseInfoView = ({ info, data }) => {
  const [records, setRecords] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [recordsCache, setRecordsCache] = useState({});
  const [items, setItems] = useState([]);
  const [isDraggingAny, setIsDraggingAny] = useState(false);

  const navigate = useNavigate();
  const { personID } = useParams();

  /* Assign stable drag IDs */
  useEffect(() => {
    setItems(
      info?.data?.map((item) => ({
        ...item,
        _dragId: String(item.id),
      })) || [],
    );
  }, [info]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 }, // prevents click → drag
    }),
  );

  const handleDragEnd = (event) => {
    setIsDraggingAny(false);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setItems((prev) => {
      const oldIndex = prev.findIndex((i) => i._dragId === active.id);
      const newIndex = prev.findIndex((i) => i._dragId === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const fetchRecords = (caseID) => {
    if (!caseID) return;

    if (recordsCache[caseID]) {
      setRecords(recordsCache[caseID]);
      return;
    }

    setIsLoading(true);
    fetch(`/api/getEDSSCase?id=${caseID}&person=${data?.PERSON_ID}&preview=yes`)
      .then((res) => res.json())
      .then((d) => {
        setRecords(d);
        setRecordsCache((prev) => ({ ...prev, [caseID]: d }));
      })
      .finally(() => setIsLoading(false));
  };

  const formatKey = (key) =>
    key
      .replace(/_/g, " ")
      .replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());

  const handleDragStart = () => {
    setIsDraggingAny(true);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
    >
      <SortableContext
        items={items.map((i) => i._dragId)}
        strategy={rectSortingStrategy}
      >
        {items.map((item) => (
          <SortableCard key={item._dragId} id={item._dragId}>
            {({ attributes, listeners }) => (
              <Popover
                placement="right"
                trigger="hover"
                closeOnBlur={false}
                onOpen={() => fetchRecords(item.id)}
                isOpen={isDraggingAny ? false : undefined}
              >
                <PopoverTrigger>
                  <Box
                    p={4}
                    bg="gray.100"
                    boxShadow="md"
                    borderRadius="md"
                    maxW="350px"
                  >
                    {/* Header */}
                    <Flex justify="space-between" align="center" mb={2}>
                      {/* Drag Handle */}
                      <Flex
                        {...attributes}
                        {...listeners}
                        align="center"
                        gap={1}
                        cursor="grab"
                      >
                        <MdDragIndicator />
                        <Text fontSize="sm">Drag</Text>
                      </Flex>

                      {/* Navigation Link */}
                      <FaLink
                        size={18}
                        cursor="pointer"
                        onClick={(e) => {
                          e.stopPropagation(); // 🔑 critical
                          navigate(`/CaseView/${item.id}/${personID}`);
                        }}
                      />
                    </Flex>

                    {/* Content */}
                    {Object.entries(item)
                      .filter(([key]) => key !== "id" && key !== "_dragId")
                      .map(([key, value]) => {
                        const date = moment(
                          value,
                          ["YYYY-MM-DD", "YYYYMMDD", moment.ISO_8601],
                          true,
                        );
                        const isDate = date.isValid();

                        return (
                          <Flex key={key} fontSize="sm">
                            <Box w="40%">{formatKey(key)}</Box>
                            <Box mx={1}>:</Box>
                            <Box w="60%">
                              {isDate
                                ? date.format("MMM D, YYYY")
                                : (value ?? "N/A")}
                            </Box>
                          </Flex>
                        );
                      })}
                  </Box>
                </PopoverTrigger>

                <PopoverContent w="1200px" maxH="700px" overflowY="auto">
                  <PopoverArrow />
                  <PopoverBody>
                    <DisplayCaseInformation
                      data={records}
                      isLoading={isLoading}
                    />
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            )}
          </SortableCard>
        ))}
      </SortableContext>
    </DndContext>
  );
};

export default CaseInfoView;
